import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { 
  Calendar, 
  Clock, 
  Users, 
  ArrowLeft, 
  Save, 
  BookOpen,
  AlertTriangle,
  Eye,
  XCircle
} from 'lucide-react';

const EditClassPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { classId } = useParams();
  const [classTypes, setClassTypes] = useState([]);
  const [loadingClassTypes, setLoadingClassTypes] = useState(true);
  
  // Original class data
  const [originalData, setOriginalData] = useState(null);
  const [members, setMembers] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    classTypeId: '',
    day: 'Monday',
    time: '9:00 AM',
    totalSpots: 5,
    status: 'active'
  });
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const days = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  const timeSlots = [
    '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
    '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
    '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM'
  ];

  const cancelReasons = [
    'Insufficient enrollment',
    'Instructor unavailable',
    'Scheduling conflict',
    'Technical issues',
    'Other'
  ];

  // Load class data
  useEffect(() => {
    const loadClassData = async () => {
      if (!user || !classId) return;

      try {
        setLoading(true);
        setError(null);

        // Verify admin status
        const adminStatus = await api.admin.verifyAdmin(user.$id);
        if (!adminStatus) {
          navigate('/classes');
          return;
        }

        // Load class types
        const types = await api.classTypes.getActive();
        setClassTypes(types);

        // Load class details
        const classDetails = await api.classes.getDetails(classId);
        if (!classDetails) {
          throw new Error('Class not found');
        }

        // Check if class can be edited
        if (classDetails.status !== 'active') {
          setError(`Cannot edit ${classDetails.status} classes`);
          return;
        }

        setOriginalData(classDetails);
        setFormData({
          classTypeId: classDetails.classTypeId || '',
          day: classDetails.day,
          time: classDetails.time,
          totalSpots: classDetails.totalSpots || 5,
          status: classDetails.status
        });

        // Load member details
        if (classDetails.members && classDetails.members.length > 0) {
          const memberDetails = await api.admin.getClassMembers(classDetails.members);
          setMembers(memberDetails);
        }

      } catch (err) {
        console.error('Error loading class data:', err);
        setError(err.message || 'Failed to load class details');
      } finally {
        setLoading(false);
      }
    };

    loadClassData();
  }, [user, classId, navigate]);

  // Check for changes
  useEffect(() => {
    if (!originalData) return;

    const changed = 
      formData.classType !== originalData.type ||
      formData.day !== originalData.day ||
      formData.time !== originalData.time ||
      formData.totalSpots !== originalData.totalSpots;

    setHasChanges(changed);
  }, [formData, originalData]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'totalSpots' ? parseInt(value) || 0 : value
    }));
  };

  // Validate capacity change
  const validateCapacityChange = () => {
    const currentEnrollment = members.length;
    if (formData.totalSpots < currentEnrollment) {
      return {
        valid: false,
        message: `Cannot reduce capacity below current enrollment (${currentEnrollment} students)`
      };
    }
    return { valid: true };
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Validate form
      if (!formData.classType || !formData.day || !formData.time || !formData.totalSpots) {
        setError('Please fill in all fields');
        return;
      }

      // Validate capacity
      const capacityCheck = validateCapacityChange();
      if (!capacityCheck.valid) {
        setError(capacityCheck.message);
        return;
      }

      if (formData.totalSpots < 1 || formData.totalSpots > 20) {
        setError('Total spots must be between 1 and 20');
        return;
      }
      
      // Call API to update class
      const response = await api.admin.updateClass(classId, {
        day: formData.day,
        time: formData.time,
        type: formData.classType,
        totalSpots: formData.totalSpots
      });
      
      if (response.success) {
        // Navigate back to class view with success message
        navigate(`/admin/class/${classId}`, {
          state: { message: 'Class updated successfully' }
        });
      } else {
        throw new Error(response.message || 'Failed to update class');
      }
    } catch (err) {
      console.error('Error updating class:', err);
      setError(err.message || 'Failed to update class. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel class
  const handleCancelClass = async () => {
    if (!cancelReason) {
      setError('Please provide a reason for cancellation');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const response = await api.admin.cancelClass(classId, cancelReason);
      
      if (response.success) {
        navigate(`/admin/class/${classId}`, {
          state: { message: 'Class cancelled successfully' }
        });
      } else {
        throw new Error(response.message || 'Failed to cancel class');
      }
    } catch (err) {
      console.error('Error cancelling class:', err);
      setError(err.message || 'Failed to cancel class. Please try again.');
    } finally {
      setIsSubmitting(false);
      setShowCancelModal(false);
    }
  };

  // Handle navigation
  const handleBack = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(`/admin/class/${classId}`);
      }
    } else {
      navigate(`/admin/class/${classId}`);
    }
  };

  const handleViewClass = () => {
    navigate(`/admin/class/${classId}`);
  };

  // Get changes summary
  const getChangesSummary = () => {
    if (!originalData || !hasChanges) return null;

    const changes = [];
    if (formData.classType !== originalData.type) {
      changes.push(`Type: ${originalData.type} → ${formData.classTypeId}`);
    }
    if (formData.day !== originalData.day) {
      changes.push(`Day: ${originalData.day} → ${formData.day}`);
    }
    if (formData.time !== originalData.time) {
      changes.push(`Time: ${originalData.time} → ${formData.time}`);
    }
    if (formData.totalSpots !== originalData.totalSpots) {
      changes.push(`Capacity: ${originalData.totalSpots} → ${formData.totalSpots} spots`);
    }

    return changes;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-purple-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-400">Loading class details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !originalData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
        <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
          <div className="container mx-auto p-4 flex justify-between items-center">
            <h1 className="text-xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
                Admin Dashboard
              </span>
            </h1>
          </div>
          <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
        </header>

        <main className="container mx-auto py-8 px-4">
          <div className="max-w-md mx-auto text-center">
            <XCircle size={48} className="text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Cannot Edit Class</h2>
            <p className="text-gray-400 mb-6">{error}</p>
            <Button variant="primary" onClick={() => navigate('/admin')}>
              Back to Dashboard
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold">
            <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
              Admin Dashboard
            </span>
          </h1>
          
          <div className="flex items-center space-x-4">
            <span className="text-gray-300 text-sm hidden sm:inline">
              Admin: {user?.name}
            </span>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Back button and view class */}
          <div className="flex justify-between mb-6">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-gray-400 hover:text-white flex items-center"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Class
            </Button>

            <Button
              variant="outline"
              onClick={handleViewClass}
              className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:bg-opacity-10 flex items-center"
            >
              <Eye size={16} className="mr-2" />
              View Class
            </Button>
          </div>

          {/* Page title */}
          <div className="text-center mb-8">
            <div className="inline-block text-6xl font-light text-purple-400 opacity-20 mb-2">
              编辑课程
            </div>
            <h2 className="text-2xl font-bold">Edit Class</h2>
            <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-6 p-4 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {/* Current enrollment warning */}
          {members.length > 0 && (
            <div className="mb-6 p-4 bg-yellow-900 bg-opacity-20 border border-yellow-700 rounded-lg">
              <div className="flex items-start">
                <AlertTriangle size={20} className="text-yellow-400 mr-3 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-300 mb-1">Class Has Enrolled Students</h4>
                  <p className="text-yellow-200 text-sm">
                    This class has {members.length} enrolled student{members.length !== 1 ? 's' : ''}. 
                    Changes to schedule will affect them. Consider notifying students of any changes.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Edit class form */}
          <form onSubmit={handleSubmit}>
            <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-700">
                <h3 className="font-semibold text-lg flex items-center">
                  <Calendar size={20} className="mr-2" />
                  Class Details
                </h3>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Class Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <BookOpen size={16} className="inline mr-2" />
                    Class Type *
                  </label>
                  <select
                    name="classTypeId"
                    value={formData.classTypeId}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-600 bg-[rgba(255,255,255,0.05)] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                    required
                  >
                    {classTypes.map(classType => (
                      <option key={classType.$id} value={classType.$id} className="bg-gray-800">
                        {classType.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Day */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Calendar size={16} className="inline mr-2" />
                    Day of Week *
                  </label>
                  <select
                    name="day"
                    value={formData.day}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-600 bg-[rgba(255,255,255,0.05)] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                    required
                  >
                    {days.map(day => (
                      <option key={day} value={day} className="bg-gray-800">
                        {day}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Clock size={16} className="inline mr-2" />
                    Time *
                  </label>
                  <select
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-600 bg-[rgba(255,255,255,0.05)] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                    required
                  >
                    {timeSlots.map(time => (
                      <option key={time} value={time} className="bg-gray-800">
                        {time}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Total Spots */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Users size={16} className="inline mr-2" />
                    Maximum Students *
                  </label>
                  <input
                    type="number"
                    name="totalSpots"
                    value={formData.totalSpots}
                    onChange={handleChange}
                    min={members.length || 1}
                    max="20"
                    className="w-full p-3 border border-gray-600 bg-[rgba(255,255,255,0.05)] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                    required
                  />
                  <p className="text-gray-400 text-sm mt-1">
                    Currently enrolled: {members.length} students
                    {members.length > 0 && ` (minimum capacity: ${members.length})`}
                  </p>
                </div>
              </div>
            </div>

            {/* Changes preview */}
            {hasChanges && (
              <div className="mt-6 bg-blue-900 bg-opacity-20 border border-blue-700 rounded-lg p-4">
                <h4 className="font-medium text-blue-300 mb-2">Changes to be made:</h4>
                <ul className="text-blue-200 text-sm space-y-1">
                  {getChangesSummary().map((change, index) => (
                    <li key={index}>• {change}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Form actions */}
            <div className="flex justify-between mt-8">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCancelModal(true)}
                className="border-red-500 text-red-400 hover:bg-red-500 hover:bg-opacity-10 flex items-center"
              >
                <XCircle size={16} className="mr-2" />
                Cancel Class
              </Button>
              
              <div className="flex space-x-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleBack}
                  className="bg-gray-700 hover:bg-gray-600 border border-gray-600"
                >
                  Cancel
                </Button>
                
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={isSubmitting}
                  disabled={!hasChanges}
                  className="bg-purple-600 hover:bg-purple-700 flex items-center"
                >
                  <Save size={16} className="mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          </form>
        </div>
      </main>

      {/* Cancel Class Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4 text-red-400">Cancel Class</h3>
            <p className="text-gray-300 mb-4">
              This will permanently cancel the class and cannot be undone. 
              {members.length > 0 && ` All ${members.length} enrolled students will be notified.`}
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Reason for cancellation *
              </label>
              <select
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full p-3 border border-gray-600 bg-[rgba(255,255,255,0.05)] rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-white"
                required
              >
                <option value="" className="bg-gray-800">Select a reason...</option>
                {cancelReasons.map(reason => (
                  <option key={reason} value={reason} className="bg-gray-800">
                    {reason}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
              >
                Keep Class
              </Button>
              <Button
                variant="primary"
                onClick={handleCancelClass}
                isLoading={isSubmitting}
                className="bg-red-600 hover:bg-red-700"
              >
                Cancel Class
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditClassPage;